import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { gql } from 'apollo-boost';
import { useLazyQuery } from '@apollo/react-hooks';
import { themes, PartnerClinicDetails } from 'kb-shared';
import InputField from '../../../../components/InputField';
import SelectInput from '../../../../components/SelectInput';
import Button from '../../../../components/Button/NewButton';
import Pagination from '../../../../components/Pagination';
import { ReactComponent as SearchIcon } from '../../../../assets/icons/search.svg';
import { ReactComponent as MapIcon } from '../../../../assets/icons/map-icon.svg';
import PartnerClinicCard from './PartnerClinicCard';

const Title = styled.div`
  ${themes.fonts.DomaineDisplay};
  font-size: 24px;
  color: ${themes.colors.KBNight};
`;

const Text = styled.div`
  z-index: 1;
  ${themes.fonts.FoundersGrotesk};
  font-size: 16px;
  color: ${themes.colors.KBNight};
`;

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 24px;
  max-width: 100%;
  @media screen and (max-width: ${themes.breakpoints.largePhonePortrait}) {
    width: 100%;
  }
`;

const ZipCodeContainer = styled.div`
  display: flex;
  align-items: center;
  padding-top: 20px;
  @media screen and (max-width: ${themes.breakpoints.largePhonePortrait}) {
    flex-direction: column;
    width: 100%;
    padding-top: 0px;
  }
`;

const KindbodyNetworkContainer = styled.div`
  display: flex;
  align-items: center;
  @media screen and (max-width: ${themes.breakpoints.tabletLandscape}) {
    flex-direction: column;
    width: 100%;
  }
  font-size: 14px;
  color: ${themes.colors.KBNight};
  ${themes.fonts.FoundersGrotesk};
  width: 75%;
  margin-top: 100px;
  div {
    padding: 10px 10px;
  }
`;

const UnableToFindContainer = styled.div`
  margin: 40px 0px;
  display: flex;
  flex-direction: column;
  align-items: center;
  div {
    text-align: center;
  }

  > * {
    padding: 20px 0px;
  }
`;

interface SpaceContentProps {
  centerContent?: boolean;
}

const PartnerClinicsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-content: center;
  align-items: stretch;
  margin-top: 40px;
  max-width: 1100px;
  ${(props: SpaceContentProps) => `
    justify-content: ${props.centerContent ? 'space-evenly' : 'flex-start'};
  `}
  @media screen and (max-width: ${themes.breakpoints.largePhonePortrait}) {
    flex-direction: column;
  }
  min-height: 100px;
`;

const SelectInputWrapper = styled.div`
  margin-top: -15px;
  min-width: 300px;
  display: flex;
`;

const SearchIconWrapper = styled.div`
  margin: 6px 10px;
  border-radius: 50%;
  height: 60px;
  width: 60px;
  background-color: ${themes.colors.KBYellow};
  cursor: pointer;
  svg {
    transform: translate(10px, 10px);
  }
  svg > path {
    fill: ${themes.colors.KBNight};
  }
`;

const ZIP_CODE = gql`
  query zipcodeDistances($zipcodeStr: String!, $distance: Int!, $units: String!) {
    zipClinicDistance(zipcodeStr: $zipcodeStr, distance: $distance, units: $units) {
      name
      address
      distance
      tier
      contactName
      contactEmail
      state
      city
    }
  }
`;

interface Option {
  value: string;
  label: string;
}

const DISTANCE_OPTIONS = [
  { label: '10 Miles', value: '10' },
  { label: '30 Miles', value: '30' },
  { label: '60 Miles', value: '60' }
];

interface ResponseData {
  zipClinicDistance: Array<PartnerClinicDetails>;
}

const ITEMS_PER_PAGE = 9;

interface Props {
  onSelected: (partnerClinicSelected: PartnerClinicDetails) => void;
}

const PartnerLocationSearch = (props: Props) => {
  const [zipCode, setZipCode] = useState('');
  const [currentPage, setPage] = useState(0);
  const [distance, setDistance] = useState<Option>(DISTANCE_OPTIONS[2]);
  const [getZipcodeDistances, { data }] = useLazyQuery<ResponseData>(ZIP_CODE);

  useEffect(() => {
    if (window.analytics && Array.isArray(data?.zipClinicDistance)) {
      if (
        // @ts-ignore Object is possibly 'undefined'.
        data.zipClinicDistance.length > 0
      ) {
        window.analytics.track('Partner Clinic Search Successful', {
          // eslint-disable-next-line @typescript-eslint/camelcase
          zip_code: zipCode,
          radius: parseInt(distance.value)
        });
      } else {
        window.analytics.track('Partner Clinic Search Error', {
          // eslint-disable-next-line @typescript-eslint/camelcase
          zip_code: zipCode,
          radius: parseInt(distance.value)
        });
      }
    }
  }, [data]);

  return (
    <Container>
      <ZipCodeContainer>
        <div style={{ width: 300 }}>
          <InputField
            label="Enter your Zip Code"
            type="number"
            onChange={newValue => setZipCode(newValue.toString())}
            value={zipCode}
          />
        </div>
        <SelectInputWrapper>
          <div style={{ width: 200 }}>
            <SelectInput
              extractTitle={item => (item ? item.label : '')}
              selectedOption={distance}
              type={'select'}
              onSelect={(select: Option) => setDistance(select)}
              options={DISTANCE_OPTIONS}
              isNew={true}
            />
          </div>
          <SearchIconWrapper
            onClick={() => {
              const radius = parseInt(distance.value);
              if (window.analytics) {
                window.analytics.track('Partner Clinic Searched', {
                  // eslint-disable-next-line @typescript-eslint/camelcase
                  zip_code: zipCode,
                  radius
                });
              }
              getZipcodeDistances({
                variables: {
                  zipcodeStr: zipCode,
                  distance: radius,
                  units: 'miles'
                }
              });
            }}
          >
            <SearchIcon width={40} height={40} fill="black" />
          </SearchIconWrapper>
        </SelectInputWrapper>
      </ZipCodeContainer>
      {!data && (
        <KindbodyNetworkContainer>
          <div>
            <Title>The Kindbody Network</Title>
            <Text>
              Kindbody's signature clinics offer the full spectrum of health and fertility care
              under one roof. We offer a variety of services including fertility, fertility
              preservation, gynecology, pre and post partum care, surrgocay and adoption servives,
              along with comprehension wellness and behavioral health in modern, tech-enabled
              clinics.
            </Text>
            <Text>
              In addition to our locations, we partner with over 200 clinics across the country to
              ensure our members get the access and ocverage they need.
            </Text>
            <Text>
              <b>Search our network to get started!</b>
            </Text>
          </div>
          <div>
            <MapIcon />
          </div>
        </KindbodyNetworkContainer>
      )}
      {data?.zipClinicDistance.length == 0 && (
        <UnableToFindContainer>
          <Title>
            We're sorry, we were unable to find any results for your search.
            <br />
            Please try again or message us directly so we can coordinate your care!
          </Title>
          <Button
            text="Contact Us"
            onClick={() => {
              if (window.analytics) {
                window.analytics.track('Secure Message Page');
              }
              window.location.href = '/messages?category=admin&subject=Partner+Clinic+Request';
            }}
          />
        </UnableToFindContainer>
      )}
      <PartnerClinicsContainer
        centerContent={data ? data.zipClinicDistance.length % ITEMS_PER_PAGE <= 2 : false}
      >
        {data?.zipClinicDistance &&
          data.zipClinicDistance
            .slice(currentPage * ITEMS_PER_PAGE, (currentPage + 1) * ITEMS_PER_PAGE)
            .map((partnerClinicDetails, idx) => (
              <PartnerClinicCard
                key={idx}
                onClick={() => {
                  if (window.analytics) {
                    window.analytics.track('Partner Clinic Selected', {
                      name: partnerClinicDetails.name,
                      address: partnerClinicDetails.address,
                      city: partnerClinicDetails.city,
                      state: partnerClinicDetails.state,
                      // eslint-disable-next-line @typescript-eslint/camelcase
                      page_number: currentPage + 1
                    });
                  }
                  props.onSelected(partnerClinicDetails);
                }}
                partnerClinicDetails={partnerClinicDetails}
              />
            ))}
      </PartnerClinicsContainer>
      {data?.zipClinicDistance && data?.zipClinicDistance.length > ITEMS_PER_PAGE && (
        <Pagination
          currentPage={currentPage}
          numberOfPages={Math.ceil(data?.zipClinicDistance.length / ITEMS_PER_PAGE)}
          setPage={setPage}
        />
      )}
    </Container>
  );
};

export default PartnerLocationSearch;
